// ✅ ViewInvoiceDrawer con fechas reales desde suscripciones y formato de factura digital actualizado
import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { CheckCircleIcon, XCircleIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';
import logo from '../../assets/img/acerca-de.svg';

const ViewInvoiceDrawer = ({ order, onClose, onOrderUpdated }) => {
  const [status, setStatus] = useState(order.status);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderItems, setOrderItems] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [itemsRes, subsRes] = await Promise.all([
          api.get(`/api/orders/${order.id}/items`),
          api.get(`/api/subscriptions/order/${order.id}`)
        ]);
        setOrderItems(itemsRes.data);
        setSubscriptions(subsRes.data);
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError('No se pudieron cargar los ítems o suscripciones de la orden');
      }
    };
    fetchData();
  }, [order.id]);

  const handleUpdate = async () => {
    setLoading(true);
    setError('');
    try {
      await api.put(`/api/orders/${order.id}`, { status });
      onOrderUpdated({ ...order, status });
    } catch (err) {
      console.error('Error al actualizar orden:', err);
      setError('No se pudo actualizar la orden');
    } finally {
      setLoading(false);
    }
  };

  const AlertIcon = status === 'pagado' ? CheckCircleIcon : status === 'cancelado' ? XCircleIcon : ExclamationCircleIcon;
  const isPaid = status === 'pagado';
  const paymentDateFormatted = order.paid_at ? new Date(order.paid_at).toLocaleString('es-ES') : null;

  const getSubscriptionRange = (planId) => {
    const sub = subscriptions.find(s => s.service_plan_id === planId);
    if (!sub) return null;
    const from = new Date(sub.last_renewed_at || sub.start_date);
    const to = new Date(sub.next_billing_date || sub.end_date);
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return `Todos los meses (${from.toLocaleDateString('es-ES', options)} - ${to.toLocaleDateString('es-ES', options)})`;
  };

  const calculateSubtotal = () => {
    return orderItems.reduce((acc, item) => acc + item.quantity * item.price, 0);
  };

  return (
    <Dialog open={true} onClose={onClose} className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <Dialog.Panel className="relative w-full max-w-[800px] bg-white shadow-xl m-0 flex flex-col overflow-hidden">
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h2 className="text-sm font-medium text-gray-900 uppercase">Factura</h2>
          <button onClick={onClose} className="bg-gray-100 text-gray-800 p-1.5 rounded-full hover:bg-gray-200">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {status && (
          <div className={`border-b px-6 py-4 flex items-start gap-2 ${isPaid ? 'bg-green-50 border-green-200 text-green-800' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`}>
            <AlertIcon className="w-5 h-5 mt-0.5" />
            <div>
              <p className="text-sm font-medium">{isPaid ? 'Factura pagada' : 'Estado de la factura'}</p>
              {isPaid && paymentDateFormatted && (
                <p className="text-sm mt-1">Esta factura se pagó en su totalidad el {paymentDateFormatted}.</p>
              )}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="flex justify-between items-start">
            <div className="text-sm text-gray-700">
              <img src={logo} alt="Logo Hoy Vemos" className="w-48 mb-2" />
              <p className="text-lg font-bold text-gray-900 mb-1">HV-{order.id}</p>
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Facturado a</p>
              <p>{order.user_name}</p>
              <p>{order.email}</p>
            </div>
            <div className="text-right text-sm text-gray-700">
              <p className="font-medium">HOY Vemos</p>
              <p>Series y Películas</p>
              <p>San José, Costa Rica</p>
              <p>+506 8301 2068</p>
              <p className="mt-2 font-medium">Fecha</p>
              <p>{new Date(order.created_at).toLocaleDateString('es-ES')}</p>
              <p className="mt-2 font-medium">Estado</p>
              <div className="mt-1">
                <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-black text-white capitalize">
                  {status}
                </span>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Detalle de la Orden</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-gray-700">
                <thead className="bg-black text-white text-left">
                  <tr>
                    <th className="px-4 py-2">Servicio</th>
                    <th className="px-4 py-2">Descripción</th>
                    <th className="px-4 py-2">Precio</th>
                    <th className="px-4 py-2">Cant.</th>
                    <th className="px-4 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {orderItems.map((item) => (
                    <tr key={item.id} className="border-b border-gray-200">
                      <td className="px-4 py-2 font-medium text-gray-900">{item.service_name_snapshot}</td>
                      <td className="px-4 py-2">{getSubscriptionRange(item.service_plan_id) || '-'}</td>
                      <td className="px-4 py-2">${parseFloat(item.price).toFixed(2)}</td>
                      <td className="px-4 py-2">{item.quantity}</td>
                      <td className="px-4 py-2 font-semibold">${(item.quantity * item.price).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
            <div className="bg-gray-50 p-4 rounded border border-gray-200 space-y-2">
              <p><span className="font-medium">Pago con:</span> SINPE Móvil</p>
              <p><span className="font-medium">Documento:</span> 85696212236852</p>
              <p><span className="font-medium">Banco:</span> Banco BCR</p>
              <p><span className="font-medium">Fecha de pago:</span> 15 JUN 2025</p>
              <p><span className="font-medium">Estado:</span> Completado</p>
            </div>
            <div className="bg-white p-4 rounded border border-gray-200 space-y-2">
              <div className="flex justify-between">
                <p>Subtotal</p>
                <p>${calculateSubtotal().toFixed(2)}</p>
              </div>
              <div className="flex justify-between font-medium text-gray-900">
                <p>Total</p>
                <p>${order.total ? parseFloat(order.total).toFixed(2) : '0.00'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 p-4 flex justify-between">
          <button onClick={onClose} className="text-sm font-medium text-gray-700 bg-gray-100 rounded px-4 py-2 hover:bg-gray-200">Cerrar</button>
          <button onClick={handleUpdate} disabled={loading} className="text-sm font-medium text-white bg-black rounded px-4 py-2 hover:bg-gray-800">
            {loading ? 'Guardando...' : 'Ir a Factura'}
          </button>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};

export default ViewInvoiceDrawer;
